import React from 'react'
import WorkspaceLogo from 'modules/workspace/components/WorkspaceLogo'
import AvatarContainer from 'modules/shares/components/AvatarContainer'
import useUserProfile from 'modules/user/useUserProfile'
import { ButtonUnstyled } from 'components/Button'
import { Flex } from 'components/Box'
import { ReactComponent as SharedIcon } from 'icons/globe-16.svg'
import Tooltip from 'components/Tooltip'
import ModalConsumer from 'containers/ModalManager/ModalConsumer'
import {
  PublicWorkspaceFragment,
  ShareInfoFragment,
  useGetShareMembershipsQuery,
  VersionFragment,
} from 'gqlTypes'
import styled from 'styled-components'
import { withContentRect, MeasuredComponentProps } from 'react-measure'
import ShareSettingsModal, {
  ShareForSettingsModal,
} from '../ShareSettingsModal'
import OtherMembers from './OtherMembers'
import theme from 'styles/theme'
import { isPersonalWorkspace } from 'modules/workspace/utils'

const DEFAULT_MAX_AVATARS = 6

/**
 * Types
 */
interface OwnerProps {
  workspace: Pick<
    PublicWorkspaceFragment,
    'avatar' | 'name' | 'userIsOwner' | 'type'
  >
  openShareSettingsModal?: () => void
  userCanUpdateSettings: boolean
}

interface PublicShareAvatarContainerProps {
  userCanUpdateSettings: boolean
  openShareSettingsModal: () => void
  publicAccessLevel: ShareForSettingsModal['publicAccessLevel']
}

export interface ShareForMemberList
  extends Pick<
    ShareInfoFragment,
    | 'identifier'
    | 'userCanUpdateSettings'
    | 'publicUrl'
    | 'publicAccessLevel'
    | 'publicCommentsEnabled'
    | 'userCanUpdateCommentsEnabled'
    | 'allowedPrivateAccessLevels'
    | 'allowedWorkspaceAccessLevels'
    | 'project'
    | 'name'
    | 'createdAt'
    | 'updatedAt'
    | 'libraryEnabled'
    | 'workspaceAccessLevel'
    | 'workspaceCommentsEnabled'
    | 'userCanUpdateWorkspacePermissions'
    | 'isDraft'
    | 'workspace'
  > {}

interface VersionForMemberList {
  currentVersion: VersionFragment | null
  latestVersion: VersionFragment | null
}

export interface MemberListProps {
  share: ShareForMemberList
  version: VersionForMemberList
}

/**
 * Styles
 */
const Divider = styled.div`
  display: flex;
  align-self: center;
  flex-shrink: 0;
  height: 28px;
  width: 1px;
  background: linear-gradient(to bottom, #ffffff 0%, #000 50%, #ffffff 100%);
  opacity: 0.1;
  margin-left: 8px;
  margin-right: 8px;
`

const ButtonAvatarContainer: React.FC = ({ children, ...props }) => (
  <ButtonUnstyled {...props}>
    <AvatarContainer>{children}</AvatarContainer>
  </ButtonUnstyled>
)

const Owner: React.FunctionComponent<OwnerProps> = props => {
  const { workspace, userCanUpdateSettings, openShareSettingsModal } = props

  const workspaceLogo = isPersonalWorkspace(workspace) ? (
    <WorkspaceLogo.Icon
      data-testid="owner-avatar-button"
      size="24px"
      icon="person"
    />
  ) : (
    <WorkspaceLogo
      data-testid="owner-avatar-button"
      size="24px"
      src={workspace.avatar?.small}
      workspaceName={workspace.name}
    />
  )

  if (userCanUpdateSettings) {
    return (
      <ButtonUnstyled onClick={openShareSettingsModal}>
        {workspaceLogo}
      </ButtonUnstyled>
    )
  }

  return workspaceLogo
}

const PublicShareAvatarContainer = ({
  userCanUpdateSettings,
  openShareSettingsModal,
  publicAccessLevel,
}: PublicShareAvatarContainerProps) => {
  const AvatarContainerComp = userCanUpdateSettings
    ? ButtonAvatarContainer
    : AvatarContainer
  const tooltipContent = (
    <span>
      Anyone with the link can{' '}
      {publicAccessLevel === 'VIEW' ? 'view' : 'inspect'}
    </span>
  )

  return (
    <Tooltip placement="bottom" spacing="14px" content={tooltipContent}>
      <AvatarContainerComp
        {...(userCanUpdateSettings ? { onClick: openShareSettingsModal } : {})}
        data-testid="link-avatar-container">
        {/* Explicit width and height otherwise it's cut off in Safari */}
        <SharedIcon style={{ pointerEvents: 'none' }} width={14} height={14} />
      </AvatarContainerComp>
    </Tooltip>
  )
}

const MemberList: React.FunctionComponent<
  MemberListProps & MeasuredComponentProps
> = ({ share, version, measureRef, contentRect }) => {
  const userProfile = useUserProfile(true)
  const { data, error } = useGetShareMembershipsQuery({
    variables: { shareIdentifier: share.identifier },
  })

  if (error) {
    return null
  }

  const {
    userCanUpdateSettings,
    publicAccessLevel,
    workspace,
    workspaceAccessLevel,
  } = share

  let totalCount = data?.share?.memberships?.meta.totalCount || 0

  let memberships = data?.share?.memberships?.entries || []
  const userMembership = data?.share?.myMembership

  if (userMembership) {
    memberships = memberships.filter(
      entry =>
        entry.workspaceMembership?.user?.identifier !==
        userProfile.data?.me.identifier
    )
    memberships.unshift(userMembership)

    if (data?.share?.memberships?.entries.length !== memberships.length) {
      // Avoid showing unaccurate total count when
      // showing current user first
      totalCount = totalCount - 1
    }
  }

  const isPublic = !!publicAccessLevel && publicAccessLevel !== 'NONE'
  const avatarSize = theme.memberList.avatarSize
  const avatarMargin = theme.memberList.avatarMargin
  let maxAvatars = DEFAULT_MAX_AVATARS

  if (contentRect?.offset?.width) {
    const widthPerAvatar = avatarSize + avatarMargin
    maxAvatars =
      Math.floor(contentRect.offset.width / widthPerAvatar) - Number(isPublic) // Account for the public avatar container
  }

  const tooltipContent =
    workspaceAccessLevel === 'DEFAULT' ? (
      <span>
        {workspace.name} Workspace Editors can edit and Viewers can inspect
      </span>
    ) : (
      <span>
        Everyone at {workspace.name} can{' '}
        {workspaceAccessLevel?.toLowerCase() || 'view'}
      </span>
    )

  return (
    <Flex ref={measureRef} height={28}>
      <ModalConsumer>
        {({ showModal }) => {
          const openShareSettingsModal = () => {
            showModal(ShareSettingsModal, {
              share,
              version,
              openPermissions: true,
            })
          }
          return (
            <>
              <Tooltip
                placement="bottom"
                spacing="14px"
                content={tooltipContent}
                disabled={data?.share?.userIsOwner}>
                <Owner
                  workspace={workspace}
                  userCanUpdateSettings={userCanUpdateSettings}
                  openShareSettingsModal={openShareSettingsModal}
                />
              </Tooltip>
              <Divider />
              {isPublic && (
                <PublicShareAvatarContainer
                  userCanUpdateSettings={userCanUpdateSettings}
                  openShareSettingsModal={openShareSettingsModal}
                  publicAccessLevel={publicAccessLevel}
                />
              )}
              <OtherMembers
                maxAvatars={maxAvatars - 1}
                userCanUpdateSettings={userCanUpdateSettings}
                memberships={memberships}
                totalCount={totalCount}
                openShareSettingsModal={openShareSettingsModal}
                avatarSize={avatarSize}
                isPublic={isPublic}
              />
            </>
          )
        }}
      </ModalConsumer>
    </Flex>
  )
}

export default withContentRect('offset')(MemberList)
